/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  const BULK_ORDER_TITLE_SORT_ORDER = [
    'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL', '7XL', '8XL', '9XL', '10XL',
    'XST', 'ST', 'MT', 'LT', 'XLT', '2XLT', '3XLT', '4XLT', '5XLT', '6XLT', '7XLT', '8XLT', '9XLT', '10XLT',
    'XSXT', 'SXT', 'MXT', 'LXT', 'XLXT', '2XLXT', '3XLXT', '4XLXT', '5XLXT', '6XLXT', '7XLXT', '8XLXT', '9XLXT', '10XLXT',
  ];

  const BULK_ORDER_TITLE_INDEX = new Map(
    BULK_ORDER_TITLE_SORT_ORDER.map((label, i) => [label, i])
  );

  function normalizeBulkOrderSizeLabel(label) {
    if (label == null || label === '') return '';
    const s = typeof label === 'string' ? label : String(label);
    return s.trim().replace(/\s+/g, '').toUpperCase();
  }

  function parseBulkOrderIntFragment(fragment) {
    const n = parseInt(fragment, 10);
    return Number.isFinite(n) ? n : null;
  }

  function bulkOrderSizeSortKey(rawLabel, stableIndex) {
    const t = normalizeBulkOrderSizeLabel(rawLabel);
    if (!t) return [3, stableIndex];

    const titleIdx = BULK_ORDER_TITLE_INDEX.get(t);
    if (titleIdx !== undefined) return [0, titleIdx, stableIndex];

    let m = /^(\d+)W$/.exec(t);
    if (m) {
      const waist = parseBulkOrderIntFragment(m[1]);
      if (waist !== null) return [1, waist, 0, 0, t];
      return [2, stableIndex, t];
    }

    m = /^(\d+)X(\d+)$/.exec(t);
    if (m) {
      const waist = parseBulkOrderIntFragment(m[1]);
      const inseam = parseBulkOrderIntFragment(m[2]);
      if (waist !== null && inseam !== null) return [1, waist, inseam, 1, t];
      return [2, stableIndex, t];
    }

    m = /^(\d+)F$/.exec(t);
    if (m) {
      const waist = parseBulkOrderIntFragment(m[1]);
      if (waist !== null) return [1, waist, 0, 2, t];
      return [2, stableIndex, t];
    }

    m = /^(\d+)X(\d+)F$/.exec(t);
    if (m) {
      const waist = parseBulkOrderIntFragment(m[1]);
      const inseam = parseBulkOrderIntFragment(m[2]);
      if (waist !== null && inseam !== null) return [1, waist, inseam, 3, t];
      return [2, stableIndex, t];
    }

    return [2, stableIndex, t];
  }

  function compareBulkOrderSortKeys(a, b) {
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i += 1) {
      const av = a[i];
      const bv = b[i];
      if (av === bv) continue;
      if (av === undefined) return -1;
      if (bv === undefined) return 1;
      if (typeof av === 'number' && typeof bv === 'number') {
        const aNaN = Number.isNaN(av);
        const bNaN = Number.isNaN(bv);
        if (aNaN || bNaN) {
          if (aNaN && bNaN) continue;
          return aNaN ? 1 : -1;
        }
        if (av < bv) return -1;
        if (av > bv) return 1;
        continue;
      }
      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1;
      if (as > bs) return 1;
    }
    return 0;
  }

  function bulkOrderSizeSortKeySafe(rawLabel, stableIndex) {
    try {
      return bulkOrderSizeSortKey(rawLabel, stableIndex);
    } catch {
      return [2, stableIndex, ''];
    }
  }

  function safeSortBulkOrderContainer(container) {
    try {
      if (!container || container.nodeType !== 1) return;
      const items = Array.from(container.children).filter((el) => el.classList && el.classList.contains('bulk-order-item'));
      if (items.length < 2) return;

      const decorated = items.map((el, i) => ({
        el,
        stableIndex: i,
        key: bulkOrderSizeSortKeySafe(el.dataset.sizeValue != null ? el.dataset.sizeValue : '', i),
      }));

      decorated.sort((a, b) => {
        const cmp = compareBulkOrderSortKeys(a.key, b.key);
        if (cmp !== 0) return cmp;
        return a.stableIndex - b.stableIndex;
      });

      const frag = document.createDocumentFragment();
      decorated.forEach(({ el }) => frag.appendChild(el));
      container.appendChild(frag);
    } catch {}
  }

  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.selectedOptionIds = this.getSelectedOptionIds();
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variantRequestId = 0;

      this.bulkOrderMode = this.dataset.bulkOrder === 'true';
      if (this.bulkOrderMode) {
        this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
        const variantMapId = this.dataset.variantMapId;
        const variantMapData = (variantMapId && window.variantMapData && window.variantMapData[variantMapId])
          ? window.variantMapData[variantMapId]
          : (this.dataset.variantMap || '');
        this.variantMap = this.parseVariantMap(variantMapData);
        this.productId = this.dataset.productId;
        this.productFormId = this.dataset.productFormId;
        this.variantsCount = parseInt(this.dataset.variantsCount || '0', 10);
        this.needsApiFetch = this.dataset.needsApiFetch === 'true';
        this.productHandle = this.dataset.productHandle;
        this.selectedNonSizeOptions = {};
        this.initBulkOrder();
      }

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    disconnectedCallback() {
      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
        this.buttonObserver = null;
      }
      if (this._pendingAvailabilityRequest) {
        this._pendingAvailabilityRequest.abort();
        this._pendingAvailabilityRequest = null;
      }
      if (this._availabilityUpdateTimeout) {
        clearTimeout(this._availabilityUpdateTimeout);
        this._availabilityUpdateTimeout = null;
      }
      if (this.quantityUpdateTimeout) {
        clearTimeout(this.quantityUpdateTimeout);
        this.quantityUpdateTimeout = null;
      }
      if (this._initTimeouts) {
        this._initTimeouts.forEach((timeout) => clearTimeout(timeout));
        this._initTimeouts = [];
      }
      this.removeListeners();
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      if (!this.bulkOrderMode) {
        this.updateAddToCartButton();
      }
      this.updateSwatchLabel();
      this.updateAvailability();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus && evt.target.id) {
            const focusSelector = `#${evt.target.id}-button, input#${evt.target.id}`;
            try {
              document.querySelector(focusSelector)?.focus();
            } catch {
              // Invalid selector, skip focus restoration
            }
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            if (el.bulkOrderMode && el.quantityInputs) {
              el.updateAddToCartButton();
            } else {
              el.updateVariantContent();
            }
          });
        }, 10);
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;

      if (this.bulkOrderMode) {
        this.addBtn.disabled = false;
        const addToCartText = this.addBtn.dataset.addToCartText || 'Add to cart';
        if (this.addBtn.textContent.includes('Choose')
            || this.addBtn.textContent === theme.strings?.noVariant
            || this.addBtn.textContent.trim() === '') {
          this.addBtn.textContent = addToCartText;
        } else if (this.addBtn.textContent !== addToCartText && addToCartText) {
          this.addBtn.textContent = addToCartText;
        }
        return;
      }

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.section.querySelector('variant-picker [type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }

    parseVariantMap(mapString) {
      if (!mapString) return {};
      const variantMap = {};
      const variantEntries = mapString.split('|||').filter((entry) => entry);

      variantEntries.forEach((entry) => {
        const parts = entry.split('::');
        if (parts.length >= 3) {
          const key = parts[0];
          const variantId = parts[1];
          if (!/^\d+$/.test(variantId)) return;
          const available = parts[2] === 'true';
          const inventoryQty = parseInt(parts[3] || '0', 10);
          variantMap[key] = { id: variantId, available, inventory_quantity: inventoryQty };
        }
      });

      return variantMap;
    }

    initBulkOrder() {
      safeSortBulkOrderContainer(this.querySelector('.bulk-order-btns'));

      this.quantityInputs = this.querySelectorAll('.bulk-order-quantity-input');
      this.bulkOrderItems = this.querySelectorAll('.bulk-order-item');
      this.errorElement = this.querySelector('.js-bulk-order-error');
      this.nonSizeSelects = Array.from(this.optionSelectors).filter((sel) => {
        const index = parseInt(sel.dataset.index, 10) - 1;
        return index !== this.sizeOptionIndex;
      });

      this.quantityUpdateTimeout = null;
      const debouncedUpdate = () => {
        clearTimeout(this.quantityUpdateTimeout);
        this.quantityUpdateTimeout = setTimeout(() => {
          this.updateBulkOrderTotal();
          this.updateAddToCartButton();
        }, 100);
      };

      this.quantityInputs.forEach((input) => {
        input.addEventListener('input', debouncedUpdate);
        input.addEventListener('change', () => {
          clearTimeout(this.quantityUpdateTimeout);
          this.updateBulkOrderTotal();
          this.updateAddToCartButton();
        });
        input.addEventListener('wheel', (e) => {
          if (document.activeElement === input) e.preventDefault();
        }, { passive: false });
        input.addEventListener('focus', () => {
          if (input.value === '0') input.value = '';
        });
        input.addEventListener('blur', () => {
          if (input.value === '') input.value = '';
        });
        input.addEventListener('input', () => {
          const value = input.value;
          if (value === '') return;
          if (!/^[1-9]\d*$/.test(value)) input.value = '';
        });
      });

      const clearBulkQuantities = () => {
        if (!this.quantityInputs) return;
        this.quantityInputs.forEach((input) => {
          input.value = '';
          delete input.dataset.variantId;
        });
        this.updateBulkOrderTotal();
      };

      this.nonSizeSelects.forEach((selector) => {
        const select = selector.querySelector('select, .custom-select__native');
        if (select) {
          select.addEventListener('change', () => {
            clearBulkQuantities();
            this.updateBulkOrderAvailabilityViaAPI();
            this.updateAddToCartButton();
          });
        }
        selector.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.addEventListener('change', () => {
            clearBulkQuantities();
            this.updateBulkOrderAvailabilityViaAPI();
            this.updateAddToCartButton();
          });
        });
      });

      const productForm = this.getProductForm();
      if (productForm) {
        const variantIdInput = productForm.querySelector('input[name="id"]');
        if (variantIdInput) variantIdInput.removeAttribute('required');

        productForm.addEventListener('submit', (evt) => {
          if (this.bulkOrderMode && this.hasBulkOrderQuantities()) {
            evt.preventDefault();
            evt.stopPropagation();
            evt.stopImmediatePropagation();
            this.handleBulkOrderSubmit(evt);
            return false;
          }
        }, true);
      }

      const quantityWrapper = this.section.querySelector('quantity-wrapper');
      if (quantityWrapper) {
        quantityWrapper.closest('.input-row')?.style.setProperty('display', 'none');
      }

      if (productForm) {
        const addBtn = productForm.querySelector('[name="add"]');
        if (addBtn) {
          const addToCartText = addBtn.dataset.addToCartText || 'Add to cart';
          this.buttonObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'childList' || mutation.type === 'characterData') {
                const currentText = addBtn.textContent.trim();
                if (currentText.includes('Choose') || currentText === theme.strings?.noVariant) {
                  addBtn.textContent = addToCartText;
                }
              }
            });
          });
          this.buttonObserver.observe(addBtn, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
      }

      this._initTimeouts = [];
      this._initTimeouts.push(setTimeout(() => {
        this.updateBulkOrderAvailabilityViaAPI();
      }, 100));

      this.updateBulkOrderTotal();
      this._initTimeouts.push(setTimeout(() => {
        this.updateAddToCartButton();
      }, 50));
    }

    hasBulkOrderQuantities() {
      if (!this.quantityInputs) return false;
      return Array.from(this.quantityInputs).some((input) => {
        const qty = parseInt(input.value, 10) || 0;
        return qty > 0;
      });
    }

    updateBulkOrderTotal() {
      if (!this.quantityInputs) return;
    }

    async updateBulkOrderAvailability() {
      if (!this.bulkOrderItems || !this.quantityInputs) return;

      this.selectedNonSizeOptions = {};
      this.nonSizeSelects.forEach((selector) => {
        const index = parseInt(selector.dataset.index, 10) - 1;
        const selected = selector.querySelector('input:checked, .custom-select__option[aria-selected="true"]');
        if (selected) {
          this.selectedNonSizeOptions[index] = selected.value || selected.dataset.value;
        }
      });

      const promises = Array.from(this.bulkOrderItems).map(async (item) => {
        const sizeValue = item.dataset.sizeValue;
        const input = item.querySelector('.bulk-order-quantity-input');
        if (!sizeValue || !input) return;

        const variant = await this.findVariantForSize(sizeValue);

        if (variant && variant.available) {
          input.disabled = false;
          item.classList.remove('bulk-order-item--disabled');
        } else {
          input.disabled = true;
          item.classList.add('bulk-order-item--disabled');
          if (parseInt(input.value, 10) > 0) {
            input.value = '';
            this.updateBulkOrderTotal();
          }
        }
      });

      await Promise.all(promises);
    }

    async findVariantForSize(sizeValue) {
      const selectedCount = Object.keys(this.selectedNonSizeOptions).length;
      if (selectedCount === 0) return { available: true };

      const keysToTry = this.buildVariantKeysForSize(sizeValue);
      for (const key of keysToTry) {
        if (this.variantMap[key]) return this.variantMap[key];
      }

      if (!this._variantsBySize) {
        this._variantsBySize = {};
        Object.entries(this.variantMap).forEach(([key, variant]) => {
          const keyParts = key.split('||');
          const sizeValueInKey = keyParts[this.sizeOptionIndex];
          if (sizeValueInKey) {
            if (!this._variantsBySize[sizeValueInKey]) this._variantsBySize[sizeValueInKey] = [];
            this._variantsBySize[sizeValueInKey].push({ key, variant, keyParts });
          }
        });
      }

      const variantsForSize = this._variantsBySize[sizeValue];
      if (!variantsForSize) return null;

      for (const { variant, keyParts } of variantsForSize) {
        let matches = true;
        for (const [optionIndex, optionValue] of Object.entries(this.selectedNonSizeOptions)) {
          const idx = parseInt(optionIndex, 10);
          if (idx >= keyParts.length || keyParts[idx] !== optionValue) {
            matches = false;
            break;
          }
        }
        if (matches) return variant;
      }

      return null;
    }

    async updateBulkOrderAvailabilityViaAPI() {
      if (!this.bulkOrderItems || !this.section) return;

      if (this._pendingAvailabilityRequest) {
        this._pendingAvailabilityRequest.abort();
      }

      const controller = new AbortController();
      this._pendingAvailabilityRequest = controller;

      clearTimeout(this._availabilityUpdateTimeout);
      this._availabilityUpdateTimeout = setTimeout(async () => {
        try {
          const selectedOptionValueIds = [];
          this.optionSelectors.forEach((selector) => {
            if (selector.dataset.selectorType === 'bulk-order') return;
            const selected = selector.querySelector('input:checked, .custom-select__option[aria-selected="true"]');
            if (selected) {
              const optionValueId = selected.dataset.optionValueId || selected.dataset.valueId;
              if (optionValueId) selectedOptionValueIds.push(optionValueId);
            }
          });

          const sectionId = this.dataset.sectionId || this.section.dataset.sectionId;
          if (!sectionId) {
            console.error('Section ID is missing. Cannot update availability.');
            return;
          }

          const productUrl = this.dataset.productUrl || this.section.dataset.productUrl || window.location.pathname;
          const params = selectedOptionValueIds.length > 0
            ? `&option_values=${selectedOptionValueIds.join(',')}`
            : '';

          const response = await fetch(`${productUrl}?section_id=${sectionId}${params}`, {
            signal: controller.signal,
          });

          if (!response.ok) throw new Error(`Failed to fetch section: ${response.status}`);

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newBulkOrderBtns = doc.querySelector('.bulk-order-btns');

          if (newBulkOrderBtns) {
            newBulkOrderBtns.querySelectorAll('.bulk-order-item').forEach((newItem) => {
              const sizeValue = newItem.dataset.sizeValue;
              const existingItem = Array.from(this.bulkOrderItems).find((item) => item.dataset.sizeValue === sizeValue);

              if (existingItem) {
                const input = existingItem.querySelector('.bulk-order-quantity-input');
                const isAvailable = !newItem.classList.contains('bulk-order-item--unavailable');

                if (isAvailable) {
                  input.disabled = false;
                  existingItem.classList.remove('bulk-order-item--disabled');
                  if (newItem.dataset.variantId) {
                    input.dataset.variantId = newItem.dataset.variantId;
                    existingItem.dataset.variantId = newItem.dataset.variantId;
                  }
                } else {
                  input.disabled = true;
                  existingItem.classList.add('bulk-order-item--disabled');
                  if (parseInt(input.value, 10) > 0) {
                    input.value = '';
                    this.updateBulkOrderTotal();
                  }
                }
              }
            });
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          if (!this.needsApiFetch) {
            this.updateBulkOrderAvailability();
          } else {
            console.warn('Failed to update bulk order availability:', error);
          }
        } finally {
          if (this._pendingAvailabilityRequest === controller) {
            this._pendingAvailabilityRequest = null;
          }
        }
      }, 300);
    }

    buildVariantKeysForSize(sizeValue) {
      const allOptionSelectors = Array.from(this.optionSelectors);
      const maxOptionIndex = Math.max(
        ...allOptionSelectors.map((sel) => parseInt(sel.dataset.index, 10) - 1),
        this.sizeOptionIndex
      );
      const totalOptions = maxOptionIndex + 1;
      const parts = [];
      for (let i = 0; i < totalOptions; i += 1) {
        parts.push(i === this.sizeOptionIndex ? sizeValue : (this.selectedNonSizeOptions[i] || ''));
      }
      return [parts.join('||')];
    }

    buildVariantKeyWithOptions(selectedOptions, sizeValue) {
      const parts = [];
      for (let i = 0; i < this.optionSelectors.length; i += 1) {
        parts.push(i === this.sizeOptionIndex ? sizeValue : (selectedOptions[i] || ''));
      }
      return parts.join('||');
    }

    async handleBulkOrderSubmit(evt) {
      if (evt) {
        evt.preventDefault();
        evt.stopPropagation();
      }

      const form = this.getProductForm();
      if (!form) return;

      const submitBtn = form.querySelector('[name="add"]');
      if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.hideBulkOrderError();
      const formError = form.querySelector('.js-form-error');
      if (formError) formError.hidden = true;

      form.querySelectorAll(':invalid').forEach((input) => {
        input.setCustomValidity('');
      });

      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput) variantIdInput.removeAttribute('required');

      const { items, error } = await this.getBulkOrderItems();

      if (error) {
        this.showBulkOrderError(error);
        return;
      }

      if (items.length === 0) {
        form.requestSubmit();
        return;
      }

      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('is-loading');
      }

      try {
        const formData = new FormData();

        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.id);
          formData.append(`items[${index}][quantity]`, item.quantity);
          if (item.properties) {
            Object.keys(item.properties).forEach((key) => {
              formData.append(`items[${index}][properties][${key}]`, item.properties[key]);
            });
          }
        });

        const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el);
        const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sectionIds.join(','));

        if (!theme?.routes?.cartAdd) {
          throw new Error('Cart add route is not configured. Please check theme configuration.');
        }

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        let data;
        try {
          data = await response.json();
        } catch {
          throw new Error('Invalid response from server. Please try again.');
        }

        if (data.status) {
          const errorMsg = typeof data.description === 'string'
            ? data.description
            : data.message || 'Failed to add items to cart';
          this.showBulkOrderError(errorMsg);
          if (submitBtn) {
            submitBtn.removeAttribute('aria-disabled');
            submitBtn.classList.remove('is-loading');
          }
          return;
        }

        if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
          sectionsToUpdate.forEach((el) => {
            if (el.updateFromCartChange) {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }

        if (document.querySelector('.template-cart')) {
          const cartForm = document.querySelector('cart-form');
          if (cartForm && cartForm.enableAjaxUpdate) cartForm.refresh();
        } else if (theme.settings.afterAddToCart === 'notification') {
          const notification = document.getElementById('AddedNotification')?.content?.firstElementChild?.cloneNode(true);
          if (notification) {
            notification.dataset.productTitle = data.product_title || '';
            const notificationContainer = document.querySelector('.pageheader--sticky') || document.querySelector('body');
            notificationContainer?.appendChild(notification);
          }
        } else if (theme.settings.afterAddToCart === 'drawer') {
          document.querySelector('.js-cart-drawer')?.open(submitBtn);
        } else if (theme.settings.afterAddToCart === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        }

        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantIds: items.map((item) => item.id) },
        }));

        this.quantityInputs.forEach((input) => {
          input.value = '';
        });
        this.updateBulkOrderTotal();
        this.clearLineItemPropertyFields();

        if (submitBtn) {
          submitBtn.classList.add('is-success');
          submitBtn.removeAttribute('aria-disabled');
          setTimeout(() => {
            submitBtn.classList.remove('is-loading');
            submitBtn.classList.remove('is-success');
          }, 2000);
        }
      } catch (submitError) {
        console.error('Bulk order submission error:', submitError);
        const errorMsg = submitError instanceof Error ? submitError.message : 'An error occurred while adding items to cart';
        this.showBulkOrderError(errorMsg);
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('is-loading');
        }
      }
    }

    async getBulkOrderItems() {
      const items = [];
      const currentSelectedNonSizeOptions = {};
      this.nonSizeSelects.forEach((selector) => {
        const index = parseInt(selector.dataset.index, 10) - 1;
        const selected = selector.querySelector('input:checked, .custom-select__option[aria-selected="true"]');
        if (selected) {
          currentSelectedNonSizeOptions[index] = selected.value || selected.dataset.value;
        }
      });

      const allNonSizeSelected = this.nonSizeSelects.length === 0
        || this.nonSizeSelects.every((sel) => {
          const index = parseInt(sel.dataset.index, 10) - 1;
          return currentSelectedNonSizeOptions[index];
        });

      let hasVariantIds = false;
      this.quantityInputs.forEach((input) => {
        const quantity = parseInt(input.value, 10) || 0;
        if (quantity > 0 && input.dataset.variantId) hasVariantIds = true;
      });

      if (!allNonSizeSelected && !hasVariantIds) {
        return { items: [], error: 'Please select all options before adding to cart' };
      }

      const MAX_QUANTITY_PER_SIZE = 999;

      for (const input of this.quantityInputs) {
        const quantity = parseInt(input.value, 10) || 0;
        if (quantity <= 0) continue;
        if (input.disabled) continue;

        if (quantity > MAX_QUANTITY_PER_SIZE) {
          return { items: [], error: `Maximum quantity per size is ${MAX_QUANTITY_PER_SIZE}` };
        }

        const sizeValue = input.dataset.sizeValue;
        let variantId = input.dataset.variantId;

        if (variantId && !/^\d+$/.test(variantId)) continue;

        if (variantId) {
          items.push({ id: variantId, quantity });
        } else if (allNonSizeSelected && !this.needsApiFetch) {
          const variantKey = this.buildVariantKeyWithOptions(currentSelectedNonSizeOptions, sizeValue);
          const variant = this.variantMap[variantKey];

          if (variant && variant.available) {
            if (variant.inventory_quantity > 0 && quantity > variant.inventory_quantity) {
              return { items: [], error: `Only ${variant.inventory_quantity} available for ${sizeValue}` };
            }
            items.push({ id: variant.id, quantity });
          } else if (variant && !variant.available) {
            return { items: [], error: `${sizeValue} is out of stock` };
          } else {
            return { items: [], error: `Variant not found for ${sizeValue}. Please select all options.` };
          }
        } else {
          console.warn(`Skipping ${sizeValue}: No variant ID available. Please ensure all options are selected.`);
        }
      }

      if (items.length === 0) {
        return { items: [], error: 'Please enter quantities for available sizes' };
      }

      let properties;
      try {
        properties = await this.collectCustomProperties();
      } catch (propertyError) {
        return { items: [], error: propertyError.message || 'Error processing custom properties' };
      }

      if (properties && Object.keys(properties).length > 0) {
        items.forEach((item) => {
          item.properties = { ...properties };
        });
      }

      return { items, error: null };
    }

    async collectCustomProperties() {
      const form = this.getProductForm();
      if (!form) return {};

      const properties = {};
      const processedNames = new Set();

      for (const input of form.querySelectorAll('[name^="properties["]')) {
        const nameMatch = input.name.match(/^properties\[(.+)\]$/);
        if (!nameMatch) continue;

        const propertyKey = nameMatch[1];
        if (processedNames.has(propertyKey)) continue;

        let propertyValue = null;

        if (input.type === 'file') {
          if (input.files && input.files.length > 0) {
            const file = input.files[0];
            const MAX_FILE_SIZE = 10 * 1024 * 1024;
            if (file.size > MAX_FILE_SIZE) {
              throw new Error(`File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
            }
            propertyValue = file;
          } else if (input.value) {
            propertyValue = input.value;
          }
        } else if (input.type === 'radio') {
          const checkedRadio = form.querySelector(`[name="properties[${propertyKey}]"]:checked`);
          if (checkedRadio) propertyValue = checkedRadio.value;
        } else if (input.type === 'checkbox') {
          if (input.checked) propertyValue = input.value;
        } else {
          propertyValue = input.value;
        }

        if (propertyValue !== null && propertyValue !== '' && propertyValue !== undefined) {
          properties[propertyKey] = propertyValue;
        }

        processedNames.add(propertyKey);
      }

      return properties;
    }

    showBulkOrderError(message) {
      if (this.errorElement) {
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }
    }

    hideBulkOrderError() {
      if (this.errorElement) {
        this.errorElement.hidden = true;
        this.errorElement.textContent = '';
      }
    }

    clearLineItemPropertyFields() {
      const form = this.getProductForm();
      if (!form) return;

      form.querySelectorAll('[name^="properties["]').forEach((input) => {
        if (input.type === 'file') {
          input.value = '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (input.type === 'radio' || input.type === 'checkbox') {
          input.checked = false;
        } else if (input.tagName === 'TEXTAREA' || input.type === 'text') {
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });

      const firstRadioGroups = new Set();
      form.querySelectorAll('[name^="properties["][type="radio"]').forEach((radio) => {
        if (!firstRadioGroups.has(radio.name)) {
          firstRadioGroups.add(radio.name);
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
